import React, { Suspense } from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { Provider } from 'react-redux'
import { QueryClient, QueryClientProvider } from 'react-query'
import { ThemeProvider, createTheme } from '@mui/material/styles'
import CssBaseline from '@mui/material/CssBaseline'

import App from './App.tsx'
import { store } from './store/index.ts'
import SessionProvider from './components/SessionProvider.tsx'
import { LoadingBar } from './components/LoadingBar.tsx'
import PWAUpdatePrompt from './components/PWAUpdatePrompt.tsx'
import { EventStreamProvider } from './contexts/EventStreamContext.tsx'
import { IS_ADMIN_PORTAL } from './utils/adminDetection.ts'

// Lazy-load admin pieces — only fetched on admin subdomain
const AdminApp = React.lazy(() => import('./AdminApp.tsx'))
const AdminThemeProvider = React.lazy(() =>
  import('./contexts/AdminThemeContext.tsx').then((m) => ({ default: m.AdminThemeProvider }))
)
const AdminThemeWrapper = React.lazy(() => import('./components/AdminThemeWrapper.tsx'))

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

// Create Material-UI theme (regular app)
const theme = createTheme({
  palette: {
    primary: {
      main: '#2e7d32', // Soccer green
    },
    secondary: {
      main: '#ff6f00', // Soccer orange
    },
  },
  typography: {
    fontFamily: 'Roboto, Arial, sans-serif',
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1280,
    },
  },
  components: {
    MuiSkeleton: {
      defaultProps: {
        animation: 'wave',
      },
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(0, 0, 0, 0.08)',
          '&::after': {
            background: 'linear-gradient(90deg, transparent, rgba(0, 0, 0, 0.08), transparent)',
            animationDuration: '1s',
            animationDelay: '0s',
          },
        },
      },
    },
  },
})

// Admin portal: swap PWA manifest, theme-color, title, and apple-mobile-web-app-title
if (IS_ADMIN_PORTAL) {
  document.title = 'Player Central Admin'

  const manifestLink = document.querySelector<HTMLLinkElement>('link[rel="manifest"]')
  if (manifestLink) {
    manifestLink.href = '/icons/admin-manifest.webmanifest'
  }

  const themeColorMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
  if (themeColorMeta) {
    themeColorMeta.content = '#5c6bc0'
  }

  const appleTitleMeta = document.querySelector<HTMLMetaElement>('meta[name="apple-mobile-web-app-title"]')
  if (appleTitleMeta) {
    appleTitleMeta.content = 'PLCN Admin Console'
  }

  const appleIconLink = document.querySelector<HTMLLinkElement>('link[rel="apple-touch-icon"]')
  if (appleIconLink) {
    appleIconLink.href = '/icons/admin-icon-192.png'
  }

  const faviconLink = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
  if (faviconLink) {
    faviconLink.href = '/icons/admin-favicon.svg'
  }
}

const RootApp: React.FC = () => {
  if (IS_ADMIN_PORTAL) {
    return (
      <Suspense fallback={null}>
        <AdminThemeProvider>
          <AdminThemeWrapper>
            <BrowserRouter>
              <LoadingBar />
              <SessionProvider>
                <AdminApp />
              </SessionProvider>
              <PWAUpdatePrompt />
            </BrowserRouter>
          </AdminThemeWrapper>
        </AdminThemeProvider>
      </Suspense>
    )
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <LoadingBar />
        <SessionProvider>
          <EventStreamProvider>
            <App />
          </EventStreamProvider>
        </SessionProvider>
        <PWAUpdatePrompt />
      </BrowserRouter>
    </ThemeProvider>
  )
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <RootApp />
      </QueryClientProvider>
    </Provider>
  </React.StrictMode>,
)
